import React, { useEffect, useRef, useState } from "react";

import {
  Copy,
  Check,
  Upload,
  Loader2,
  Wallet,
  Inbox,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  CreditCard,
  ArrowDownToLine,
  ShieldCheck,
  RefreshCw,
  Image as ImageIcon,
  FileCheck2,
  Clock3,
  XCircle,
  ChevronDown,
} from "lucide-react";

import Layout from "../components/Layout";
import { apiRequest } from "../api";

const API_BASE = String(
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api"
).replace(/\/+$/, "");

const API_ORIGIN = (function () {
  try {
    return new URL(API_BASE).origin;
  } catch (e) {
    return "";
  }
})();

function mediaUrl(filePath) {
  if (!filePath) return null;

  if (
    filePath.startsWith("http://") ||
    filePath.startsWith("https://")
  ) {
    return filePath;
  }

  return API_ORIGIN + filePath;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MIN_AMOUNT = 1;
const MAX_AMOUNT = 100000;

function getStatusMeta(status) {
  if (status === "approved") {
    return {
      color:
        "text-[#3FE0B4] bg-[#3FE0B4]/10 border-[#3FE0B4]/20",
      icon: CheckCircle2,
    };
  }

  if (status === "rejected") {
    return {
      color:
        "text-[#F07889] bg-[#F07889]/10 border-[#F07889]/20",
      icon: XCircle,
    };
  }

  return {
    color:
      "text-[#F5C563] bg-[#F5C563]/10 border-[#F5C563]/20",
    icon: Clock3,
  };
}

function SectionTitle({
  icon: Icon,
  title,
  description,
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="relative shrink-0">
        <div className="absolute inset-0 rounded-xl bg-[#22D3EE]/20 blur-md" />

        <div className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-[#22D3EE]/20 bg-gradient-to-br from-[#22D3EE]/15 to-[#2D5BFF]/15">
          <Icon size={17} className="text-[#22D3EE]" />
        </div>
      </div>

      <div className="min-w-0">
        <h2 className="text-[15px] font-bold tracking-tight text-white">
          {title}
        </h2>

        {description ? (
          <p className="mt-1 text-[11px] leading-5 text-[#7F89AC]">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default function RequestPoints() {
  const [methods, setMethods] = useState([]);
  const [methodId, setMethodId] = useState(null);
  const [loadingMethods, setLoadingMethods] = useState(true);

  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);

  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState(0);

  const [loading, setLoading] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(true);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [copied, setCopied] = useState(false);

  const fileInputRef = useRef(null);

  const selectedMethod =
    methods.find(function (m) {
      return m.id === methodId;
    }) || null;

  const loadMethods = async function () {
    try {
      setLoadingMethods(true);

      const data = await apiRequest("/payment-methods");

      const list = Array.isArray(data) ? data : [];

      setMethods(list);

      setMethodId(function (current) {
        if (
          current &&
          list.some(function (m) {
            return m.id === current;
          })
        ) {
          return current;
        }

        return list.length > 0 ? list[0].id : null;
      });
    } catch (err) {
      setMethods([]);
    } finally {
      setLoadingMethods(false);
    }
  };

  const loadData = async function () {
    try {
      setLoadingRequests(true);

      const results = await Promise.all([
        apiRequest("/users/me"),
        apiRequest("/points-requests/mine"),
      ]);

      const profile = results[0];
      const requestData = results[1];

      setBalance(Number((profile && profile.usdt_balance) || 0));

      setRequests(
        Array.isArray(requestData) ? requestData : []
      );
    } catch (err) {
      setError(err.message || "Unable to load requests.");
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(function () {
    loadMethods();
    loadData();

    const interval = setInterval(function () {
      loadData();
    }, 5000);

    return function () {
      clearInterval(interval);
    };
  }, []);

  const validateAmount = function () {
    if (!amount.trim()) {
      return "Amount is required.";
    }

    if (!/^\d+(\.\d{1,2})?$/.test(amount.trim())) {
      return "Enter a valid amount. Maximum 2 decimal places allowed.";
    }

    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount)) {
      return "Invalid amount.";
    }

    if (numericAmount < MIN_AMOUNT) {
      return "Minimum amount is " + MIN_AMOUNT + " USDT.";
    }

    if (numericAmount > MAX_AMOUNT) {
      return "Maximum amount is " + MAX_AMOUNT + " USDT.";
    }

    return "";
  };

  const validateFile = function (selectedFile) {
    if (!selectedFile) {
      return "Payment screenshot is required.";
    }

    const fileName = selectedFile.name.toLowerCase();

    const validExtension =
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg");

    if (!validExtension) {
      return "Only JPG and JPEG screenshots are allowed.";
    }

    if (selectedFile.type !== "image/jpeg") {
      return "Only JPG/JPEG image files are allowed.";
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      return "Screenshot must be smaller than 5MB.";
    }

    if (selectedFile.size === 0) {
      return "Selected file is empty.";
    }

    return "";
  };

  const handleFileChange = function (e) {
    setError("");
    setSuccess("");

    const selectedFile =
      e.target.files && e.target.files[0]
        ? e.target.files[0]
        : null;

    if (!selectedFile) {
      setFile(null);
      return;
    }

    const validationError = validateFile(selectedFile);

    if (validationError) {
      setFile(null);
      e.target.value = "";
      setError(validationError);
      return;
    }

    setFile(selectedFile);
  };

  const copyAddress = async function () {
    if (
      !selectedMethod ||
      !selectedMethod.account_number
    ) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        selectedMethod.account_number
      );

      setCopied(true);

      setTimeout(function () {
        setCopied(false);
      }, 2000);
    } catch (err) {}
  };

  const handleSubmit = async function (e) {
    e.preventDefault();

    setError("");
    setSuccess("");

    if (!methodId) {
      setError("Please select a payment method.");
      return;
    }

    const amountError = validateAmount();

    if (amountError) {
      setError(amountError);
      return;
    }

    const fileError = validateFile(file);

    if (fileError) {
      setError(fileError);
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to submit " +
        Number(amount).toFixed(2) +
        " USDT payment request?"
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append(
        "amount",
        Number(amount).toFixed(2)
      );

      formData.append(
        "payment_method_id",
        String(methodId)
      );

      formData.append("note", note.trim());

      formData.append("screenshot", file);

      await apiRequest("/points-requests", {
        method: "POST",
        body: formData,
      });

      setAmount("");
      setNote("");
      setFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setSuccess(
        "Payment request submitted successfully."
      );

      await loadData();
    } catch (err) {
      setError(
        err.message ||
          "Unable to submit payment request."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Deposit">
      <div className="relative mx-auto w-full max-w-4xl space-y-5 pb-8">

        {/* =====================================================
            AMBIENT BACKGROUND
        ====================================================== */}

        <div className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-96 overflow-hidden">
          <div
            className="absolute -left-20 -top-32 h-80 w-80 rounded-full opacity-20 blur-3xl"
            style={{
              background:
                "radial-gradient(circle, #2D5BFF, transparent 70%)",
            }}
          />

          <div
            className="absolute right-0 -top-24 h-80 w-80 rounded-full opacity-20 blur-3xl"
            style={{
              background:
                "radial-gradient(circle, #22D3EE, transparent 70%)",
            }}
          />

          <div
            className="absolute left-1/3 top-40 h-64 w-64 rounded-full opacity-10 blur-3xl"
            style={{
              background:
                "radial-gradient(circle, #8B7FE8, transparent 70%)",
            }}
          />
        </div>

        {/* =====================================================
            BALANCE CARD
        ====================================================== */}

        <div className="group relative overflow-hidden rounded-[28px] border border-[#2D5BFF]/25 bg-gradient-to-br from-[#101D4B] via-[#0D1428] to-[#0B1020] p-5 shadow-[0_20px_70px_-30px_rgba(45,91,255,0.55)] sm:p-6">

          <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-[#22D3EE]/10 blur-3xl transition-all duration-700 group-hover:bg-[#22D3EE]/20" />

          <div className="absolute bottom-0 left-1/4 h-px w-1/2 bg-gradient-to-r from-transparent via-[#22D3EE]/40 to-transparent" />

          <div className="relative">

            <div className="flex items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div className="relative">
                  <div className="absolute inset-0 rounded-2xl bg-[#22D3EE]/30 blur-lg" />

                  <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-gradient-to-br from-[#2D5BFF] to-[#22D3EE] shadow-[0_10px_30px_-10px_rgba(34,211,238,0.8)]">
                    <Wallet
                      size={20}
                      className="text-white"
                    />
                  </div>
                </div>

                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8CA0D8]">
                    Available Balance
                  </div>

                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
                      {Number(balance).toFixed(2)}
                    </span>

                    <span className="text-xs font-semibold text-[#7F9BD0]">
                      USDT
                    </span>
                  </div>
                </div>
              </div>

              <div className="hidden h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] sm:flex">
                <ArrowDownToLine
                  size={18}
                  className="text-[#22D3EE]"
                />
              </div>

            </div>

            <div className="mt-5 flex items-center gap-2 rounded-xl border border-white/5 bg-black/10 px-3 py-2.5">
              <ShieldCheck
                size={14}
                className="shrink-0 text-[#3FE0B4]"
              />

              <span className="text-[10px] leading-4 text-[#8290B5]">
                Deposit requests are securely reviewed before balance approval.
              </span>
            </div>

          </div>
        </div>

        {/* =====================================================
            MAIN FORM CARD
        ====================================================== */}

        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.08] bg-[#0B0F1B]/80 p-5 shadow-[0_25px_80px_-35px_rgba(0,0,0,0.8)] backdrop-blur-2xl sm:p-7">

          <div className="pointer-events-none absolute right-0 top-0 h-48 w-48 rounded-full bg-[#22D3EE]/[0.04] blur-3xl" />

          <div className="relative">

            <SectionTitle
              icon={Sparkles}
              title="Submit USDT Payment Request"
              description="Select a payment method, send the USDT, then submit your payment details."
            />

            {/* ALERTS */}

            {error ? (
              <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[#E5566B]/20 bg-[#E5566B]/[0.07] px-4 py-3.5 text-sm text-[#F07889] shadow-[0_10px_30px_-20px_rgba(229,86,107,0.8)]">
                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#E5566B]/10">
                  <AlertCircle size={15} />
                </div>

                <span className="leading-6">
                  {error}
                </span>
              </div>
            ) : null}

            {success ? (
              <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[#3FE0B4]/20 bg-[#3FE0B4]/[0.07] px-4 py-3.5 text-sm text-[#3FE0B4] shadow-[0_10px_30px_-20px_rgba(63,224,180,0.8)]">
                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#3FE0B4]/10">
                  <CheckCircle2 size={15} />
                </div>

                <span className="leading-6">
                  {success}
                </span>
              </div>
            ) : null}

            {/* =================================================
                PAYMENT METHOD
            ================================================== */}

            <div className="mt-6">

              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs font-semibold text-[#A7B0D0]">
                  Payment Method
                </label>

                <CreditCard
                  size={14}
                  className="text-[#596786]"
                />
              </div>

              {loadingMethods ? (
                <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.025] px-4 py-4 text-xs text-[#8B92B8]">
                  <Loader2
                    size={16}
                    className="animate-spin text-[#22D3EE]"
                  />

                  Loading payment methods...
                </div>
              ) : methods.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.025] px-4 py-4 text-xs leading-5 text-[#8B92B8]">
                  No payment methods available yet. Please check back soon.
                </div>
              ) : (
                <div className="relative">

                  <select
                    value={methodId || ""}
                    onChange={function (e) {
                      setMethodId(Number(e.target.value));
                    }}
                    className="w-full appearance-none rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3.5 pr-11 text-sm font-medium text-white outline-none transition-all duration-200 hover:border-white/15 hover:bg-white/[0.05] focus:border-[#22D3EE]/60 focus:bg-white/[0.06] focus:shadow-[0_0_0_3px_rgba(34,211,238,0.08)]"
                  >
                    {methods.map(function (m) {
                      return (
                        <option
                          key={m.id}
                          value={m.id}
                          className="bg-[#0E1220]"
                        >
                          {m.label}
                        </option>
                      );
                    })}
                  </select>

                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#7F89AC]"
                  />

                </div>
              )}
            </div>

            {/* =================================================
                SELECTED PAYMENT METHOD
            ================================================== */}

            {selectedMethod ? (
              <div className="relative mt-5 overflow-hidden rounded-[26px] border border-[#2D5BFF]/25 bg-gradient-to-b from-[#101C45] via-[#0D1428] to-[#0B101D] p-5 sm:p-6">

                <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#22D3EE]/10 blur-3xl" />

                <div className="relative">

                  <div className="mb-5 flex items-center justify-center gap-3">

                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#22D3EE] to-[#2D5BFF] text-sm font-bold text-white shadow-[0_8px_25px_-8px_rgba(34,211,238,0.8)]">
                      ₮
                    </div>

                    <div>
                      <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#7181A8]">
                        Send via
                      </div>

                      <div className="mt-0.5 text-sm font-bold text-white">
                        {selectedMethod.label}
                      </div>
                    </div>

                  </div>

                  {/* QR */}

                  <div className="flex justify-center">

                    {selectedMethod.qr_image ? (
                      <div className="relative">

                        <div className="absolute inset-0 rounded-3xl bg-[#22D3EE]/20 blur-xl" />

                        <div className="relative rounded-3xl border border-white/10 bg-white p-4 shadow-[0_20px_50px_-15px_rgba(34,211,238,0.45)]">
                          <img
                            src={mediaUrl(
                              selectedMethod.qr_image
                            )}
                            alt={
                              selectedMethod.label +
                              " QR"
                            }
                            className="h-44 w-44 object-contain sm:h-48 sm:w-48"
                          />
                        </div>

                      </div>
                    ) : (
                      <div className="flex h-44 w-44 items-center justify-center rounded-3xl border border-dashed border-white/15 bg-white/[0.025]">
                        <div className="text-center">
                          <ImageIcon
                            size={24}
                            className="mx-auto mb-2 text-[#596786]"
                          />

                          <span className="text-xs text-[#6B7394]">
                            No QR available
                          </span>
                        </div>
                      </div>
                    )}

                  </div>

                  {/* Address */}

                  <div className="mt-6">

                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7F8CAC]">
                        Wallet Address
                      </p>

                      <span className="text-[10px] text-[#596786]">
                        USDT
                      </span>
                    </div>

                    <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/25 p-2 pl-4 transition-colors hover:border-[#2D5BFF]/30">

                      <code className="min-w-0 flex-1 break-all text-[11px] leading-5 text-[#D8DEEA]">
                        {selectedMethod.account_number}
                      </code>

                      <button
                        type="button"
                        onClick={copyAddress}
                        className="flex shrink-0 items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#2D5BFF] to-[#22D3EE] px-3 py-2 text-[11px] font-bold text-white shadow-[0_8px_20px_-10px_rgba(34,211,238,0.8)] transition-all duration-200 hover:scale-[1.03] hover:shadow-[0_10px_25px_-8px_rgba(34,211,238,0.8)] active:scale-95"
                      >
                        {copied ? (
                          <Check size={13} />
                        ) : (
                          <Copy size={13} />
                        )}

                        {copied ? "Copied" : "Copy"}
                      </button>

                    </div>
                  </div>

                </div>
              </div>
            ) : null}

            {/* =================================================
                FORM
            ================================================== */}

            <form
              onSubmit={handleSubmit}
              className="mt-6 space-y-5"
            >

              {/* AMOUNT */}

              <div>

                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold text-[#A7B0D0]">
                    Amount
                  </label>

                  <span className="text-[10px] font-medium text-[#596786]">
                    USDT
                  </span>
                </div>

                <div className="group relative">

                  <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-[#22D3EE]">
                    $
                  </div>

                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={function (e) {
                      const value = e.target.value;

                      if (
                        /^\d*(\.\d{0,2})?$/.test(
                          value
                        )
                      ) {
                        setAmount(value);
                      }
                    }}
                    placeholder="0.00"
                    className="w-full rounded-2xl border border-white/10 bg-white/[0.035] py-4 pl-9 pr-16 text-base font-semibold text-white outline-none placeholder:text-[#4E5879] transition-all duration-200 hover:border-white/15 hover:bg-white/[0.045] focus:border-[#22D3EE]/60 focus:bg-white/[0.055] focus:shadow-[0_0_0_3px_rgba(34,211,238,0.08)]"
                  />

                  <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rounded-lg bg-white/[0.05] px-2 py-1 text-[10px] font-bold text-[#7181A8]">
                    USDT
                  </div>

                </div>

                <div className="mt-2 flex items-center justify-between">
                  <p className="text-[10px] text-[#596786]">
                    Minimum: {MIN_AMOUNT} USDT
                  </p>

                  <p className="text-[10px] text-[#596786]">
                    Max: {MAX_AMOUNT.toLocaleString()} USDT
                  </p>
                </div>

              </div>

              {/* NOTE */}

              <div>

                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold text-[#A7B0D0]">
                    Note
                  </label>

                  <span className="text-[10px] text-[#596786]">
                    Optional
                  </span>
                </div>

                <input
                  type="text"
                  value={note}
                  onChange={function (e) {
                    if (
                      e.target.value.length <= 255
                    ) {
                      setNote(e.target.value);
                    }
                  }}
                  placeholder="Transaction ID or any note"
                  maxLength={255}
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3.5 text-sm text-white outline-none transition-all duration-200 placeholder:text-[#4E5879] hover:border-white/15 hover:bg-white/[0.045] focus:border-[#22D3EE]/60 focus:bg-white/[0.055] focus:shadow-[0_0_0_3px_rgba(34,211,238,0.08)]"
                />

                <div className="mt-1.5 text-right text-[9px] text-[#596786]">
                  {note.length}/255
                </div>

              </div>

              {/* SCREENSHOT */}

              <div>

                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold text-[#A7B0D0]">
                    Payment Screenshot
                  </label>

                  <span className="text-[10px] text-[#596786]">
                    JPG / JPEG · Max 5MB
                  </span>
                </div>

                <label
                  htmlFor="screenshot-upload"
                  className="group relative flex cursor-pointer items-center gap-4 overflow-hidden rounded-2xl border border-dashed border-white/10 bg-white/[0.025] p-4 transition-all duration-300 hover:border-[#22D3EE]/40 hover:bg-[#22D3EE]/[0.025]"
                >

                  <div className="absolute inset-0 bg-gradient-to-r from-[#22D3EE]/0 via-[#22D3EE]/[0.025] to-[#2D5BFF]/0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#22D3EE]/10 bg-gradient-to-br from-[#2D5BFF]/15 to-[#22D3EE]/15">
                    {file ? (
                      <FileCheck2
                        size={18}
                        className="text-[#3FE0B4]"
                      />
                    ) : (
                      <Upload
                        size={18}
                        className="text-[#22D3EE] transition-transform duration-300 group-hover:-translate-y-0.5"
                      />
                    )}
                  </div>

                  <div className="relative min-w-0 flex-1">

                    {file ? (
                      <div className="truncate text-sm font-semibold text-[#3FE0B4]">
                        {file.name}
                      </div>
                    ) : (
                      <div className="text-sm font-medium text-[#B1B8D0]">
                        Choose payment screenshot
                      </div>
                    )}

                    <div className="mt-1 text-[10px] leading-4 text-[#596786]">
                      {file
                        ? "Screenshot selected successfully."
                        : "Upload a clear JPG/JPEG proof of your payment."}
                    </div>

                  </div>

                  <div className="relative hidden rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-[10px] font-semibold text-[#8B92B8] sm:block">
                    Browse
                  </div>

                </label>

                <input
                  id="screenshot-upload"
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,image/jpeg"
                  onChange={handleFileChange}
                  className="hidden"
                />

              </div>

              {/* SUBMIT */}

              <button
                type="submit"
                disabled={loading || !methodId}
                className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-r from-[#2D5BFF] via-[#22A5E8] to-[#22D3EE] py-4 text-sm font-bold text-white shadow-[0_15px_35px_-18px_rgba(34,211,238,0.9)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_45px_-18px_rgba(34,211,238,0.9)] active:translate-y-0 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
              >

                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent transition-transform duration-700 group-hover:translate-x-full" />

                {loading ? (
                  <>
                    <Loader2
                      size={17}
                      className="animate-spin"
                    />

                    Submitting...
                  </>
                ) : (
                  <>
                    <ArrowDownToLine size={17} />

                    Submit Payment Request
                  </>
                )}

              </button>

              <div className="flex items-center justify-center gap-2 text-[9px] text-[#596786]">
                <ShieldCheck size={12} />

                Your payment information is securely submitted for review.
              </div>

            </form>
          </div>
        </div>

        {/* =====================================================
            DEPOSIT ACTIVITY
        ====================================================== */}

        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.08] bg-[#0B0F1B]/80 p-5 shadow-[0_25px_80px_-35px_rgba(0,0,0,0.7)] backdrop-blur-2xl sm:p-6">

          <div className="flex items-start justify-between gap-4">

            <div className="flex items-start gap-3">

              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                <Inbox
                  size={17}
                  className="text-[#22D3EE]"
                />
              </div>

              <div>
                <h2 className="text-[15px] font-bold text-white">
                  Deposit Activity
                </h2>

                <p className="mt-1 text-[10px] leading-5 text-[#596786]">
                  Automatically refreshed every 5 seconds.
                </p>
              </div>

            </div>

            <button
              onClick={loadData}
              disabled={loadingRequests}
              className="flex shrink-0 items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[10px] font-semibold text-[#22D3EE] transition-all duration-200 hover:border-[#22D3EE]/20 hover:bg-[#22D3EE]/[0.05] disabled:opacity-50"
            >
              <RefreshCw
                size={12}
                className={
                  loadingRequests
                    ? "animate-spin"
                    : ""
                }
              />

              Refresh
            </button>

          </div>

          <div className="my-5 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          {loadingRequests ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]">
                <Loader2
                  size={20}
                  className="animate-spin text-[#22D3EE]"
                />
              </div>

              <p className="text-xs text-[#7F89AC]">
                Loading deposit activity...
              </p>
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">

              <div className="relative mb-4">
                <div className="absolute inset-0 rounded-2xl bg-[#22D3EE]/10 blur-xl" />

                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.035]">
                  <Inbox
                    size={22}
                    className="text-[#596786]"
                  />
                </div>
              </div>

              <div className="text-sm font-semibold text-[#A7B0D0]">
                No deposit requests yet
              </div>

              <div className="mt-1 text-[10px] text-[#596786]">
                Your submitted payment requests will appear here.
              </div>

            </div>
          ) : (
            <div className="space-y-3">

              {requests.map(function (request) {
                const meta = getStatusMeta(
                  request.status
                );

                const StatusIcon = meta.icon;

                return (
                  <div
                    key={request.id}
                    className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4 transition-all duration-300 hover:border-white/[0.14] hover:bg-white/[0.04]"
                  >

                    <div className="absolute inset-y-0 left-0 w-0.5 bg-gradient-to-b from-[#22D3EE] to-[#2D5BFF] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                    <div className="flex items-start justify-between gap-3">

                      <div className="min-w-0">

                        <div className="flex items-center gap-2">

                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#22D3EE]/[0.08]">
                            <Wallet
                              size={14}
                              className="text-[#22D3EE]"
                            />
                          </div>

                          <div>
                            <div className="text-sm font-bold text-white">
                              {Number(
                                request.amount_usdt ||
                                  0
                              ).toFixed(2)}{" "}
                              <span className="text-[10px] font-semibold text-[#7181A8]">
                                USDT
                              </span>
                            </div>

                            <div className="mt-0.5 text-[10px] text-[#596786]">
                              {request.created_at
                                ? new Date(
                                    request.created_at
                                  ).toLocaleString()
                                : ""}
                            </div>
                          </div>

                        </div>

                      </div>

                      <span
                        className={
                          "flex shrink-0 items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-[10px] font-bold capitalize " +
                          meta.color
                        }
                      >
                        <StatusIcon size={11} />

                        {request.status ||
                          "pending"}
                      </span>

                    </div>

                    {request.payment_method ? (
                      <div className="mt-4 rounded-xl border border-white/5 bg-black/10 px-3 py-2.5 text-[10px] text-[#7F89AC]">
                        <span className="font-semibold text-[#596786]">
                          Method:
                        </span>{" "}
                        {request.payment_method}
                      </div>
                    ) : null}

                    {request.note ? (
                      <div className="mt-2 rounded-xl border border-white/5 bg-black/10 px-3 py-2.5 text-[10px] leading-5 text-[#7F89AC] break-all">
                        <span className="font-semibold text-[#596786]">
                          Note:
                        </span>{" "}
                        {request.note}
                      </div>
                    ) : null}

                    {request.attachment_path ? (
                      <button
                        type="button"
                        onClick={function () {
                          window.open(
                            mediaUrl(
                              request.attachment_path
                            ),
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-[#22D3EE]/10 bg-[#22D3EE]/[0.04] px-3 py-2 text-[10px] font-semibold text-[#22D3EE] transition-all duration-200 hover:border-[#22D3EE]/25 hover:bg-[#22D3EE]/[0.08]"
                      >
                        <ImageIcon size={12} />

                        View Screenshot
                      </button>
                    ) : null}

                  </div>
                );
              })}

            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}